import React from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PayrollSummary = ({ payrollRecords, loading, onRefresh }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-success/10 text-success';
      case 'paid':
        return 'bg-primary/10 text-primary';
      case 'pending':
        return 'bg-warning/10 text-warning';
      case 'disputed':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount)?.toFixed(2)}`;
  };

  const formatHours = (hours) => {
    if (!hours) return '0.00';
    return parseFloat(hours)?.toFixed(2);
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-1">Payroll Summary</h2>
          <p className="text-sm text-muted-foreground">Your earnings and payment history</p>
        </div>
        <Button onClick={onRefresh} variant="outline" disabled={loading}>
          <Icon name={loading ? 'Loader2' : 'RefreshCw'} size={18} className={loading ? 'animate-spin' : ''} />
          <span>Refresh</span>
        </Button>
      </div>

      {loading && payrollRecords?.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : payrollRecords?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="DollarSign" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Payroll Records</h3>
          <p className="text-muted-foreground">Your payroll information will appear here</p>
        </div>
      ) : (
        <div className="space-y-4">
          {payrollRecords?.map((record) => (
            <div
              key={record?.id}
              className="p-6 rounded-lg border border-border hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-foreground">
                      Pay Period: {format(new Date(record?.pay_period_start), 'MMM d')} -{' '}
                      {format(new Date(record?.pay_period_end), 'MMM d, yyyy')}
                    </h3>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(record?.status)}`}>
                      {record?.status}
                    </span>
                  </div>
                  {record?.payment_date && (
                    <p className="text-sm text-muted-foreground">
                      Payment Date: {format(new Date(record.payment_date), 'MMM d, yyyy')}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-primary">
                    {formatCurrency(record?.net_pay)}
                  </div>
                  <div className="text-xs text-muted-foreground">Net Pay</div>
                </div>
              </div>

              {/* Hours Breakdown */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Regular Hours</div>
                  <div className="text-lg font-semibold text-foreground">
                    {formatHours(record?.regular_hours)}h
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Overtime Hours</div>
                  <div className="text-lg font-semibold text-warning">
                    {formatHours(record?.overtime_hours)}h
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Regular Pay</div>
                  <div className="text-lg font-semibold text-foreground">
                    {formatCurrency(record?.regular_pay)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Overtime Pay</div>
                  <div className="text-lg font-semibold text-warning">
                    {formatCurrency(record?.overtime_pay)}
                  </div>
                </div>
              </div>

              {/* Earnings & Deductions */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-foreground flex items-center gap-1">
                    <Icon name="TrendingUp" size={14} className="text-success" />
                    Earnings
                  </h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Gross Pay:</span>
                      <span className="font-medium text-foreground">{formatCurrency(record?.gross_pay)}</span>
                    </div>
                    {record?.bonus > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Bonus:</span>
                        <span className="font-medium text-success">{formatCurrency(record?.bonus)}</span>
                      </div>
                    )}
                    {record?.commission > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Commission:</span>
                        <span className="font-medium text-success">{formatCurrency(record?.commission)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-foreground flex items-center gap-1">
                    <Icon name="TrendingDown" size={14} className="text-error" />
                    Deductions
                  </h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tax:</span>
                      <span className="font-medium text-error">{formatCurrency(record?.tax_deductions)}</span>
                    </div>
                    {record?.benefit_deductions > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Benefits:</span>
                        <span className="font-medium text-error">{formatCurrency(record?.benefit_deductions)}</span>
                      </div>
                    )}
                    {record?.other_deductions > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Other:</span>
                        <span className="font-medium text-error">{formatCurrency(record?.other_deductions)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {record?.notes && (
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground italic">{record?.notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Total Earnings Summary */}
      {payrollRecords?.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground mb-1">
                {formatCurrency(
                  payrollRecords?.reduce((sum, r) => sum + (parseFloat(r?.gross_pay) || 0), 0)
                )}
              </div>
              <div className="text-xs text-muted-foreground">Total Gross Pay</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-error mb-1">
                {formatCurrency(
                  payrollRecords?.reduce(
                    (sum, r) =>
                      sum +
                      (parseFloat(r?.tax_deductions) || 0) +
                      (parseFloat(r?.benefit_deductions) || 0) +
                      (parseFloat(r?.other_deductions) || 0),
                    0
                  )
                )}
              </div>
              <div className="text-xs text-muted-foreground">Total Deductions</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary mb-1">
                {formatCurrency(
                  payrollRecords?.reduce((sum, r) => sum + (parseFloat(r?.net_pay) || 0), 0)
                )}
              </div>
              <div className="text-xs text-muted-foreground">Total Net Pay</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollSummary;